import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AssessmentIcon from "@mui/icons-material/Assessment";
import WebhookIcon from "@mui/icons-material/Webhook";
import {
  getBrokers,
  createBroker,
  updateBroker,
  deactivateBroker,
  getTrainingCatalog,
  getPolicyCatalog,
} from "api/brokerApi";
import { unwrapList, unwrapObject } from "api/unwrap";

const emptyForm = {
  code: "",
  name: "",
  parentCompany: "",
  contactPhone: "",
  contactEmail: "",
  providerPortalUrl: "",
  notes: "",
  active: true,
  requiredTrainingModuleIds: [],
  requiredPolicyDocumentIds: [],
};

const BrokerEditDialog = ({ open, onClose, broker, modules, policies, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(
        broker
          ? {
              code: broker.code || "",
              name: broker.name || "",
              parentCompany: broker.parentCompany || "",
              contactPhone: broker.contactPhone || "",
              contactEmail: broker.contactEmail || "",
              providerPortalUrl: broker.providerPortalUrl || "",
              notes: broker.notes || "",
              active: broker.active ?? true,
              requiredTrainingModuleIds: broker.requiredTrainingModuleIds || [],
              requiredPolicyDocumentIds: broker.requiredPolicyDocumentIds || [],
            }
          : emptyForm,
      );
      setError(null);
    }
  }, [open, broker]);

  const toggleModule = (id) => {
    setForm((f) => ({
      ...f,
      requiredTrainingModuleIds: f.requiredTrainingModuleIds.includes(id)
        ? f.requiredTrainingModuleIds.filter((x) => x !== id)
        : [...f.requiredTrainingModuleIds, id],
    }));
  };
  const togglePolicy = (id) => {
    setForm((f) => ({
      ...f,
      requiredPolicyDocumentIds: f.requiredPolicyDocumentIds.includes(id)
        ? f.requiredPolicyDocumentIds.filter((x) => x !== id)
        : [...f.requiredPolicyDocumentIds, id],
    }));
  };

  const handleSave = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      setError("Code and Name are required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = broker?.id
        ? await updateBroker(broker.id, form)
        : await createBroker(form);
      if (res?.error) {
        setError(res.error);
        return;
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {broker ? `Edit ${broker.name}` : "Add Broker"}
      </DialogTitle>
      <DialogContent dividers>
        {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert> : null}
        <Stack spacing={2}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Code"
              size="small"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              sx={{ flex: 1 }}
              required
              helperText="e.g. MODIVCARE, MTM, VERIDA"
            />
            <TextField
              label="Display Name"
              size="small"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              sx={{ flex: 2 }}
              required
            />
          </Box>
          <TextField
            label="Parent Company"
            size="small"
            value={form.parentCompany}
            onChange={(e) => setForm({ ...form, parentCompany: e.target.value })}
            fullWidth
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Contact Phone"
              size="small"
              value={form.contactPhone}
              onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Contact Email"
              size="small"
              value={form.contactEmail}
              onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
              sx={{ flex: 1 }}
            />
          </Box>
          <TextField
            label="Provider Portal URL"
            size="small"
            value={form.providerPortalUrl}
            onChange={(e) => setForm({ ...form, providerPortalUrl: e.target.value })}
            fullWidth
          />
          <TextField
            label="Notes"
            size="small"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            multiline
            rows={2}
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
            }
            label="Active"
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Required Trainings ({form.requiredTrainingModuleIds.length})
            </Typography>
            <Box sx={{ maxHeight: 200, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 1 }}>
              <List dense disablePadding>
                {modules.map((m, i) => (
                  <React.Fragment key={m.id}>
                    {i > 0 ? <Divider component="li" /> : null}
                    <ListItem button onClick={() => toggleModule(m.id)} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={form.requiredTrainingModuleIds.includes(m.id)}
                          size="small"
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={m.title}
                        secondary={`${m.code}${m.validForMonths ? ` · renews every ${m.validForMonths}mo` : ""}`}
                        primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                        secondaryTypographyProps={{ variant: "caption" }}
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Required Policy Acknowledgments ({form.requiredPolicyDocumentIds.length})
            </Typography>
            <Box sx={{ maxHeight: 200, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 1 }}>
              <List dense disablePadding>
                {policies.map((p, i) => (
                  <React.Fragment key={p.id}>
                    {i > 0 ? <Divider component="li" /> : null}
                    <ListItem button onClick={() => togglePolicy(p.id)} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={form.requiredPolicyDocumentIds.includes(p.id)}
                          size="small"
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={p.title}
                        secondary={`${p.code}${p.validForMonths ? ` · renews every ${p.validForMonths}mo` : " · perpetual"}`}
                        primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                        secondaryTypographyProps={{ variant: "caption" }}
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
          sx={{ backgroundColor: "#1F4E78" }}
        >
          {broker ? "Save Changes" : "Create Broker"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const BrokersPage = () => {
  const navigate = useNavigate();
  const [brokers, setBrokers] = useState([]);
  const [modules, setModules] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null = closed, "new" = create, broker obj = edit

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [b, m, p] = await Promise.all([
        getBrokers(),
        getTrainingCatalog(),
        getPolicyCatalog(),
      ]);
      setBrokers(unwrapList(b));
      setModules(unwrapList(m));
      setPolicies(unwrapList(p));
    } catch (e) {
      setError(e?.message || "Failed to load brokers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const moduleById = useMemo(() => {
    const m = {};
    modules.forEach((x) => (m[x.id] = x));
    return m;
  }, [modules]);
  const policyById = useMemo(() => {
    const m = {};
    policies.forEach((x) => (m[x.id] = x));
    return m;
  }, [policies]);

  const handleDeactivate = async (b) => {
    if (!window.confirm(`Deactivate ${b.name}?`)) return;
    try {
      await deactivateBroker(b.id);
      load();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Deactivate failed");
    }
  };

  return (
    <Stack
      spacing={2}
      sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#f8fafc", minHeight: "100%" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Brokers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage broker partners and the trainings + policies they require of your drivers.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setEditing("new")}
            sx={{ backgroundColor: "#1F4E78" }}
          >
            Add Broker
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}

      {loading && brokers.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : brokers.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
              No brokers yet. Click "Add Broker" to start.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        brokers.map((b) => (
          <Card key={b.id} variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1 }}>
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {b.name}
                    </Typography>
                    <Chip size="small" label={b.code} sx={{ fontWeight: 600 }} />
                    {!b.active ? (
                      <Chip size="small" label="Inactive" color="default" />
                    ) : null}
                  </Box>
                  {b.parentCompany ? (
                    <Typography variant="caption" color="text.secondary">
                      Parent: {b.parentCompany}
                    </Typography>
                  ) : null}
                </Box>
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/brokers/${b.id}/compliance`)}
                    title="Compliance report"
                  >
                    <AssessmentIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/brokers/${b.id}/webhook`)}
                    title="Webhook ingest config"
                  >
                    <WebhookIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setEditing(b)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeactivate(b)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 1 }}>
                {b.contactPhone ? (
                  <Typography variant="caption" color="text.secondary">
                    ☎ {b.contactPhone}
                  </Typography>
                ) : null}
                {b.contactEmail ? (
                  <Typography variant="caption" color="text.secondary">
                    ✉ {b.contactEmail}
                  </Typography>
                ) : null}
                {b.providerPortalUrl ? (
                  <Typography variant="caption" color="text.secondary">
                    🔗 <a href={b.providerPortalUrl} target="_blank" rel="noreferrer">portal</a>
                  </Typography>
                ) : null}
              </Box>

              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 1 }}>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
                    Required Trainings ({b.requiredTrainingModuleIds?.length || 0})
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                    {(b.requiredTrainingModuleIds || []).map((id) => (
                      <Chip
                        key={id}
                        size="small"
                        label={moduleById[id]?.title || id.slice(0, 8)}
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    ))}
                    {(b.requiredTrainingModuleIds || []).length === 0 ? (
                      <Typography variant="caption" color="text.secondary">none</Typography>
                    ) : null}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
                    Required Policies ({b.requiredPolicyDocumentIds?.length || 0})
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                    {(b.requiredPolicyDocumentIds || []).map((id) => (
                      <Chip
                        key={id}
                        size="small"
                        label={policyById[id]?.title || id.slice(0, 8)}
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    ))}
                    {(b.requiredPolicyDocumentIds || []).length === 0 ? (
                      <Typography variant="caption" color="text.secondary">none</Typography>
                    ) : null}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))
      )}

      <BrokerEditDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        broker={editing === "new" ? null : editing}
        modules={modules}
        policies={policies}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
    </Stack>
  );
};

export default BrokersPage;
